import { useEffect, useState } from "react";
import PhoneInput from "@/components/PhoneInput";
import { validatePhone } from "@/utils/phoneValidation";
import { toast } from "sonner";
import { X as XIcon, User } from "@phosphor-icons/react";

/**
 * ClientEditModal — add/edit a single client.
 * Props: open, initial, title, onSave(clientPayload), onClose
 */
export default function ClientEditModal({ open, initial, title, onSave, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", phone1: "", phone2: "", notes: "",
  });
  const [showPhone2, setShowPhone2] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // `phones[]` (new schema) takes precedence over legacy phone1/phone2.
      const phones = Array.isArray(initial?.phones) ? initial.phones : [];
      const p1 = phones[0] || initial?.phone1 || "";
      const p2 = phones[1] || initial?.phone2 || "";
      setForm({
        name: initial?.name || "",
        email: initial?.email || "",
        phone1: p1,
        phone2: p2,
        notes: initial?.notes || "",
      });
      setShowPhone2(!!p2);
      setSaving(false);
    }
  }, [open, initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) return toast.error("الاسم مطلوب (حرفان على الأقل)");
    const p1check = validatePhone(form.phone1);
    if (!p1check.ok) return toast.error(p1check.reason || "رقم الجوال غير صحيح");
    if (form.phone2 && String(form.phone2).replace(/\D/g, "").length > 3) {
      const p2check = validatePhone(form.phone2);
      if (!p2check.ok) return toast.error(`رقم إضافي: ${p2check.reason}`);
    }
    setSaving(true);
    try {
      const phones = [form.phone1, form.phone2].filter((p) => p && String(p).length >= 8);
      await onSave?.({
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        // Send BOTH new (phones[]) and legacy (phone1/phone2) so this modal
        // works against every endpoint variant in the codebase.
        phones,
        phone1: form.phone1,
        phone2: form.phone2 || undefined,
        notes: form.notes.trim() || undefined,
      });
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      dir="rtl"
      data-testid="client-edit-modal"
    >
      <div
        className="bg-card border border-primary/25 rounded-2xl w-full max-w-md gold-glow"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-primary/15 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 text-primary font-bold text-base">
            <User size={17} weight="duotone" /> {title || (initial ? "تعديل العميل" : "عميل جديد")}
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white" data-testid="client-edit-close">
            <XIcon size={16} weight="bold" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3">
          <label className="block">
            <div className="text-xs text-white/60 mb-1.5 text-right">اسم العميل *</div>
            <input
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="أدخل اسم العميل"
              className="mz-input"
              data-testid="client-edit-name"
            />
          </label>

          <label className="block">
            <div className="text-xs text-white/60 mb-1.5 text-right">البريد (اختياري)</div>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="example@email.com"
              className="mz-input text-right"
              dir="ltr"
              data-testid="client-edit-email"
            />
          </label>

          <div>
            <div className="text-xs text-white/60 mb-1.5 text-right">جوال العميل *</div>
            <PhoneInput
              value={form.phone1}
              onChange={(v) => set("phone1", v)}
              required
              testId="client-edit-phone1"
              defaultCountry="971"
            />
          </div>

          {!showPhone2 ? (
            <button
              type="button"
              onClick={() => setShowPhone2(true)}
              className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-primary/50 hover:border-primary hover:bg-primary/5 text-primary text-xs font-bold"
              data-testid="client-edit-add-phone2"
            >
              + إضافة رقم إضافي
            </button>
          ) : (
            <div>
              <div className="text-xs text-white/60 mb-1.5 text-right">رقم إضافي</div>
              <PhoneInput
                value={form.phone2}
                onChange={(v) => set("phone2", v)}
                testId="client-edit-phone2"
                defaultCountry="971"
              />
              <button
                type="button"
                onClick={() => { set("phone2", ""); setShowPhone2(false); }}
                className="mt-1.5 text-destructive text-[11px] hover:underline"
              >
                إزالة الرقم الإضافي
              </button>
            </div>
          )}

          <label className="block">
            <div className="text-xs text-white/60 mb-1.5 text-right">ملاحظات (اختياري)</div>
            <textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="مثال: عميل مميز · يفضّل GMC · بحاجة إلى وصل خاص…"
              rows={2}
              className="mz-input resize-none"
              data-testid="client-edit-notes"
            />
          </label>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold text-sm disabled:opacity-60"
              data-testid="client-edit-save"
            >
              {saving ? "جارٍ الحفظ…" : (initial ? "حفظ التعديلات" : "إضافة العميل")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-3 rounded-xl border border-white/20 text-white/70 text-sm"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
